import { useEffect, useState, useRef } from 'react';

/**
 * Hook to handle notification bell animation when new notifications arrive
 * @param unreadCount Current number of unread notifications
 * @param isNotificationOpen Whether the notification panel is currently open
 * @returns Object containing animation state and controls
 */
export const useNotificationBell = (unreadCount: number, isNotificationOpen: boolean = false) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const prevUnreadCount = useRef(unreadCount);
  const animationTimeout = useRef<number | null>(null);

  useEffect(() => {
    // Stop animating when notification panel is opened
    if (isNotificationOpen) {
      setIsAnimating(false);
      return;
    }

    // Only trigger animation when unreadCount increases
    if (unreadCount > prevUnreadCount.current) {
      setIsAnimating(true);
    }
    // Keep animating if there are unread notifications
    else if (unreadCount > 0) {
      setIsAnimating(true);
      return;
    }
    // Stop animating when all notifications are read
    else {
      setIsAnimating(false);
    }

    // Clear any existing timeout to prevent multiple animations
    if (animationTimeout.current) {
      window.clearTimeout(animationTimeout.current);
    }

    // Auto-reset animation after it completes (as a fallback)
    animationTimeout.current = window.setTimeout(() => {
      setIsAnimating(false);
    }, 10000); // Long timeout as fallback, but will be cleared if unreadCount changes

    // Update the previous count
    prevUnreadCount.current = unreadCount;

    // Cleanup timeout on unmount
    return () => {
      if (animationTimeout.current) {
        window.clearTimeout(animationTimeout.current);
      }
    };
  }, [unreadCount, isNotificationOpen]);

  // Manual trigger for the animation
  const triggerAnimation = () => {
    setIsAnimating(true);
    if (animationTimeout.current) {
      window.clearTimeout(animationTimeout.current);
    }
    animationTimeout.current = window.setTimeout(() => {
      setIsAnimating(false);
    }, 2000);
  };

  return {
    isAnimating,
    triggerAnimation
  };
};
